import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import ItemTable from "./ItemTable";
import TotalSection from "./TotalSection";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import {
  getCustomerEmail,
  getCustomerNotificationIssues,
} from "./invoiceCustomerContact";

const TERMS_OPTIONS = [
  { value: "", label: "Select an option" },
  { value: "15-net", label: "15 Net" },
  { value: "30-net", label: "30 Net" },
  { value: "45-net", label: "45 Net" },
  { value: "60-net", label: "60 Net" },
  { value: "end-month", label: "Due end of the month" },
  { value: "next-month", label: "Due end of next month" },
  { value: "custom", label: "Custom" },
];

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom interval" },
];

const RecurringInvoiceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const today = new Date().toLocaleDateString("en-CA");

  const [loading, setLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [intervalDays, setIntervalDays] = useState(30);
  const [nextRunDate, setNextRunDate] = useState(today);
  const [endDate, setEndDate] = useState("");
  const [invoiceDpStatus, setInvoiceDpStatus] = useState("Published");
  const [sendToCustomer, setSendToCustomer] = useState(false);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [sendPrerunNotification, setSendPrerunNotification] = useState(false);
  const [refreshPricesFromCatalog, setRefreshPricesFromCatalog] = useState(false);
  const [sendPaymentReminders, setSendPaymentReminders] = useState(true);
  const [skipIfUnpaid, setSkipIfUnpaid] = useState(true);
  const [customerNote, setCustomerNote] = useState("");
  const [shipping, setShipping] = useState(0);
  const [advance_payment, setAdvancePayment] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [itemsOptions, setItemsOptions] = useState([]);
  const [items, setItems] = useState([
    { id: 1, item: null, quantity: "", rate: "", discount: "", tax: "" },
  ]);
  const [data, setData] = useState({
    customer_name: "",
    customer_id: "",
    customer_email: "",
    terms: "30-net",
    po_number: "",
  });
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_amount: 0,
    advance_payment: 0,
    pending: 0,
    tax: 0,
    shipping: 0,
    grand_total: 0,
  });

  const getCustomerRowKey = (c) => {
    if (!c) return "";
    const cid = c.cust_ID != null ? String(c.cust_ID).trim() : "";
    if (cid) return cid;
    return c.id != null ? String(c.id) : "";
  };

  const customerBaseName = (c) =>
    c?.displayName || c?.companyName || c?.customerName || getCustomerRowKey(c) || "";

  const customerSelectLabel = (c) => {
    const name = customerBaseName(c);
    const key = getCustomerRowKey(c);
    if (name && key && name !== key) return `${name} - ${key}`;
    return name || key || "";
  };

  const getSelectedCustomer = () =>
    customers.find(
      (c) => getCustomerRowKey(c) === String(data.customer_id || "").trim()
    );

  const lineHasProduct = (row) => {
    if (!row?.item) return false;
    if (typeof row.item === "object") {
      return Boolean(
        row.item.title ||
          row.item.label ||
          row.item.value?.title ||
          row.item.value?.label
      );
    }
    return String(row.item).trim().length > 0;
  };

  const getBillableLineItems = (rows) =>
    rows.filter((row) => {
      const qty = parseFloat(row.quantity) || 0;
      const rate = parseFloat(row.rate) || 0;
      return lineHasProduct(row) && qty > 0 && rate > 0;
    });

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${url}/api/customer/all/${isLoggedIn.main_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      setCustomers(Array.isArray(json) ? json : []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTemplate = async () => {
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tpl = await res.json();
      if (!res.ok) throw new Error(tpl.message || "Failed to load billing schedule");

      setTemplateName(tpl.template_name || "");
      setFrequency(tpl.frequency || "monthly");
      setIntervalDays(tpl.interval_days || 30);
      setNextRunDate(tpl.next_run_date || today);
      setEndDate(tpl.end_date || "");
      setInvoiceDpStatus(tpl.invoice_dpstatus || "Published");
      setSendToCustomer(tpl.send_to_customer === true);
      setSendWhatsapp(tpl.send_whatsapp === true);
      setSendPrerunNotification(tpl.send_prerun_notification === true);
      setRefreshPricesFromCatalog(tpl.refresh_prices_from_catalog === true);
      setSendPaymentReminders(tpl.send_payment_reminders !== false);
      setSkipIfUnpaid(tpl.skip_if_previous_unpaid !== false);
      setCustomerNote(tpl.customer_note || "");
      setData({
        customer_name: tpl.customer_name || "",
        customer_id: tpl.customer_id || "",
        customer_email: tpl.customer_email || "",
        terms: tpl.terms || "",
        po_number: tpl.po_number || "",
      });
      if (Array.isArray(tpl.items) && tpl.items.length) {
        setItems(
          tpl.items.map((row, idx) => ({
            id: row.id ?? idx + 1,
            item: row.item,
            quantity: row.quantity,
            rate: row.rate,
            discount: row.discount,
            tax: row.tax,
            hsn: row.hsn,
          }))
        );
      }
      setTotals({
        subtotal: parseFloat(tpl.subtotal) || 0,
        discount_amount: parseFloat(tpl.discount_amount) || 0,
        advance_payment: parseFloat(tpl.advance_payment) || 0,
        pending: parseFloat(tpl.pending) || 0,
        tax: parseFloat(tpl.tax) || 0,
        shipping: parseFloat(tpl.shipping) || 0,
        grand_total: parseFloat(tpl.grand_total) || 0,
      });
      setShipping(parseFloat(tpl.shipping) || 0);
      setAdvancePayment(parseFloat(tpl.advance_payment) || 0);
    } catch (err) {
      toast.error(err.message || "Could not load billing schedule");
      navigate("/recurring-invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    if (isEdit) fetchTemplate();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!String(templateName).trim()) {
      toast.error("Schedule name is required.");
      return;
    }
    if (!data.customer_id && !String(data.customer_name).trim()) {
      toast.error("Please select a customer.");
      return;
    }
    if (!nextRunDate) {
      toast.error("Next run date is required.");
      return;
    }

    const billableLines = getBillableLineItems(items);
    if (billableLines.length === 0) {
      toast.error("Add at least one item with quantity and rate.");
      return;
    }
    if (invoiceDpStatus === "Published" && (Number(totals.grand_total) || 0) <= 0) {
      toast.error("Grand total must be greater than zero for published invoices.");
      return;
    }

    if (frequency === "custom") {
      const days = parseInt(intervalDays, 10);
      if (!Number.isFinite(days) || days < 2 || days > 365) {
        toast.error("Custom interval must be between 2 and 365 days.");
        return;
      }
    }

    if (
      invoiceDpStatus === "Published" &&
      (sendToCustomer || sendWhatsapp || sendPrerunNotification)
    ) {
      const contactCheck = getCustomerNotificationIssues(getSelectedCustomer(), {
        sendEmail: sendToCustomer || sendPrerunNotification,
        sendWhatsapp: sendWhatsapp,
        customerEmailFallback: data.customer_email,
      });
      if (!contactCheck.ok) {
        toast.error(contactCheck.message);
        return;
      }
    }

    setIsSubmitting(true);
    const payload = {
      template_name: templateName.trim(),
      customer_name: data.customer_name,
      customer_id: data.customer_id,
      customer_email: data.customer_email || "",
      customer_note: customerNote,
      terms: data.terms,
      po_number: data.po_number,
      items: billableLines.map((row) => ({
        id: row.id,
        item: row.item,
        hsn: row.hsn,
        quantity: row.quantity,
        rate: row.rate,
        discount: row.discount,
        tax: row.tax,
        amount: (
          (row.quantity || 0) *
          (row.rate || 0) *
          (1 - (row.discount || 0) / 100) *
          (1 + (row.tax || 0) / 100)
        ).toFixed(2),
      })),
      subtotal: totals.subtotal,
      discount_amount: totals.discount_amount,
      advance_payment: totals.advance_payment,
      pending: totals.pending,
      tax: totals.tax,
      shipping: totals.shipping,
      grand_total: totals.grand_total,
      frequency,
      interval_days: frequency === "custom" ? parseInt(intervalDays, 10) : null,
      next_run_date: nextRunDate,
      end_date: endDate || null,
      invoice_dpstatus: invoiceDpStatus,
      send_to_customer: sendToCustomer,
      send_whatsapp: sendWhatsapp,
      send_prerun_notification: sendPrerunNotification,
      refresh_prices_from_catalog: refreshPricesFromCatalog,
      send_payment_reminders: sendPaymentReminders,
      skip_if_previous_unpaid: skipIfUnpaid,
      user_id: isLoggedIn.main_id,
      user_name: isLoggedIn.full_name,
    };

    try {
      const endpoint = isEdit
        ? `${url}/newinvoice/recurring/${id}`
        : `${url}/newinvoice/recurring`;
      const res = await fetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const rawText = await res.text();
      let result = {};
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        const snippet = rawText.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);
        toast.error(
          res.status === 404
            ? "Auto-billing API not found. Restart Admin (npm start) with local Backend on port 5000, or deploy the new Backend routes to production."
            : snippet || "Unexpected server response"
        );
        return;
      }
      if (!res.ok) {
        toast.error(result.message || "Save failed");
        return;
      }
      toast.success(isEdit ? "Billing schedule updated" : "Auto-billing schedule created");
      navigate("/recurring-invoices");
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="product-form-page">
        <p className="product-form-page__subtitle">Loading billing schedule…</p>
      </div>
    );
  }

  return (
    <div className="product-form-page product-form-page--invoice">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">
            {isEdit ? "Edit billing schedule" : "New billing schedule"}
          </h1>
          <p className="product-form-page__subtitle">
            Invoices are created and sent automatically on the schedule you set below.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/recurring-invoices")}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="oms-add-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving…" : isEdit ? "Update schedule" : "Create schedule"}
          </button>
        </div>
      </div>

      <form className="oms-modal-form property-form product-form-page__form" onSubmit={handleSubmit}>
        <div className="oms-givepaddingfrom">
          <div className="product-form-page__section">
            <h2 className="product-form-page__section-title">Schedule</h2>
            <p className="product-form-page__section-desc">
              Name this billing schedule and choose how often invoices are created.
            </p>
          </div>

          <div className="oms-form-flex product-form-page__invoice-grid">
            <div className="oms-form-group">
              <label className="oms-form-label">Schedule name</label>
              <input
                className="oms-form-input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Monthly retainer — Acme Corp"
              />
            </div>
            <div className="oms-form-group">
              <label className="oms-form-label">Frequency</label>
              <Select
                options={FREQUENCY_OPTIONS}
                value={FREQUENCY_OPTIONS.find((o) => o.value === frequency)}
                onChange={(opt) => setFrequency(opt?.value || "monthly")}
              />
            </div>
            {frequency === "custom" && (
              <div className="oms-form-group">
                <label className="oms-form-label">Every (days)</label>
                <input
                  type="number"
                  min={2}
                  max={365}
                  className="oms-form-input"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(e.target.value)}
                />
              </div>
            )}
            <div className="oms-form-group">
              <label className="oms-form-label">Next run date</label>
              <input
                type="date"
                className="oms-form-input"
                value={nextRunDate}
                onChange={(e) => setNextRunDate(e.target.value)}
              />
            </div>
            <div className="oms-form-group">
              <label className="oms-form-label">End date (optional)</label>
              <input
                type="date"
                className="oms-form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="product-form-page__section">
            <h2 className="product-form-page__section-title">Invoice details</h2>
          </div>

          <div className="oms-form-flex product-form-page__invoice-grid">
            <div className="oms-form-group">
              <label className="oms-form-label">Customer</label>
              <Select
                value={(() => {
                  if (!data.customer_id && !data.customer_name) return null;
                  const byKey = customers.find(
                    (c) => getCustomerRowKey(c) === String(data.customer_id || "").trim()
                  );
                  if (byKey) {
                    return {
                      value: getCustomerRowKey(byKey),
                      label: customerSelectLabel(byKey),
                    };
                  }
                  return {
                    value: data.customer_id,
                    label: data.customer_name || data.customer_id,
                  };
                })()}
                onChange={(opt) => {
                  if (!opt) {
                    setData((prev) => ({
                      ...prev,
                      customer_name: "",
                      customer_id: "",
                      customer_email: "",
                    }));
                    return;
                  }
                  const match = customers.find((c) => getCustomerRowKey(c) === opt.value);
                  setData((prev) => ({
                    ...prev,
                    customer_id: opt.value,
                    customer_name: match ? customerBaseName(match) : opt.label,
                    customer_email: match ? getCustomerEmail(match) : prev.customer_email,
                  }));
                }}
                options={customers.map((c) => ({
                  value: getCustomerRowKey(c),
                  label: customerSelectLabel(c),
                }))}
              />
            </div>
            <div className="oms-form-group">
              <label className="oms-form-label">Payment terms</label>
              <Select
                options={TERMS_OPTIONS}
                value={TERMS_OPTIONS.find((o) => o.value === data.terms) || TERMS_OPTIONS[0]}
                onChange={(opt) => setData((prev) => ({ ...prev, terms: opt?.value || "" }))}
              />
            </div>
            <div className="oms-form-group">
              <label className="oms-form-label">PO number</label>
              <input
                className="oms-form-input"
                value={data.po_number}
                onChange={(e) => setData((prev) => ({ ...prev, po_number: e.target.value }))}
              />
            </div>
            <div className="oms-form-group">
              <label className="oms-form-label">Generated invoice status</label>
              <Select
                options={[
                  { value: "Published", label: "Published" },
                  { value: "Draft", label: "Draft" },
                ]}
                value={{
                  value: invoiceDpStatus,
                  label: invoiceDpStatus === "Draft" ? "Draft" : "Published",
                }}
                onChange={(opt) => setInvoiceDpStatus(opt?.value || "Published")}
              />
            </div>
          </div>

          <div className="product-form-page__section">
            <h2 className="product-form-page__section-title">Line items</h2>
          </div>
          <ItemTable
            items={items}
            setItems={setItems}
            setSubtotal={setSubtotal}
            itemsOptions={itemsOptions}
            setItemsOptions={setItemsOptions}
          />

          <div className="product-form-page__section">
            <h2 className="product-form-page__section-title">Notes & totals</h2>
          </div>
          <TotalSection
            items={items}
            shipping={shipping}
            setShipping={setShipping}
            customerNote={customerNote}
            setCustomerNote={setCustomerNote}
            setTotals={setTotals}
            advance_payment={advance_payment}
            setAdvancePayment={setAdvancePayment}
          />

          <div className="product-form-page__invoice-delivery-opt">
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={sendToCustomer}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSendToCustomer(checked);
                  if (!checked) {
                    setSendWhatsapp(false);
                    setSendPrerunNotification(false);
                  }
                }}
              />
              <span>Email invoice and pay link to customer when generated (published only)</span>
            </label>
            <p className="product-form-page__field-hint">
              When enabled, each published invoice from this schedule is emailed with the pay link.
            </p>
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={sendWhatsapp}
                disabled={!sendToCustomer}
                onChange={(e) => setSendWhatsapp(e.target.checked)}
              />
              <span>Also send invoice via WhatsApp when generated</span>
            </label>
            <p className="product-form-page__field-hint">
              Requires email delivery above and a valid customer phone number in your CRM.
            </p>
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={sendPrerunNotification}
                disabled={!sendToCustomer}
                onChange={(e) => setSendPrerunNotification(e.target.checked)}
              />
              <span>Email customer one day before the scheduled invoice date</span>
            </label>
            <p className="product-form-page__field-hint">
              Sent at 6:00 PM IST the day before the next run date.
            </p>
            {(sendToCustomer || sendWhatsapp || sendPrerunNotification) && (() => {
              const contactCheck = getCustomerNotificationIssues(getSelectedCustomer(), {
                sendEmail: sendToCustomer || sendPrerunNotification,
                sendWhatsapp: sendWhatsapp,
                customerEmailFallback: data.customer_email,
              });
              if (contactCheck.ok) return null;
              return (
                <p className="product-form-page__field-hint product-form-page__field-hint--error">
                  {contactCheck.message}
                </p>
              );
            })()}
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={refreshPricesFromCatalog}
                onChange={(e) => setRefreshPricesFromCatalog(e.target.checked)}
              />
              <span>Refresh line-item prices from product catalog on each run</span>
            </label>
            <p className="product-form-page__field-hint">
              Matches products by ID or SKU and updates rates before generating the invoice.
            </p>
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={sendPaymentReminders}
                onChange={(e) => setSendPaymentReminders(e.target.checked)}
              />
              <span>Send automated payment reminders for invoices from this schedule</span>
            </label>
            <p className="product-form-page__field-hint">
              Uses the global reminder schedule (before/on/after due date). Uncheck to opt out.
            </p>
            <label className="product-form-page__invoice-checkbox">
              <input
                type="checkbox"
                checked={skipIfUnpaid}
                onChange={(e) => setSkipIfUnpaid(e.target.checked)}
              />
              <span>Skip run if the last invoice from this schedule is still unpaid</span>
            </label>
            <p className="product-form-page__field-hint">
              Retries on the same next run date until that invoice is paid — the billing period is not skipped.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RecurringInvoiceForm;
